import { Injectable, } from '@angular/core';

import { MessageService } from '../services/message.service'
import { environment } from '../../environments/environment';
@Injectable({
    providedIn: 'root'
})

export class MasterService  {

    private headers: Record<string, any> = {
        Accept: 'application/json',
        'X-Interface-Version': environment.version
    }
    
    private host: string = "https://rockitdms.com/api/"
    
    public token = ''

    constructor(private ms: MessageService) {
        this.token = localStorage.getItem('token') ? localStorage.getItem('token') : ''
        this.host = environment.url
    }

    /**
     * Attach the authorization request header param when available.
     */
    private withAuth = () => {
        if (!this.token || (this.token.length <= 0) || this.headers['Authorization']) {
            return
        }

        this.headers['Authorization'] = `bearer ${this.token}`
    }

    /**
     * Attach the active lot to the request as a header.
     */
    private withOffset = () => {
        this.headers['X-Timezone-Offset'] = (((new Date()).getTimezoneOffset() / 60) * -1)
    }

    /**
     * Attach the active lot to the request as a header.
     */
    private withLot = () => {
        const lotId = parseInt(localStorage.getItem('lot'))
        if (!lotId || isNaN(lotId) || (lotId <= 0)) {
            return
        }

        this.headers['X-Active-Lot'] = lotId
    }

    // Funtions

    /**
     * Reset the token and auth header upon a logout/leave event.
     */
    public reset = () => {
        delete(this.headers['Authorization'])
        delete(this.headers['X-Active-Lot'])
        this.token = ''
    }

    /**
     * Add a new resource using the backend API.
     * @param action The URL invluding any path or query params.
     * @param body The resource meta data to include in the API request content body.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public post = (action: string, body: object, callback): void => {
        this.withAuth()
        this.withOffset()
        this.withLot()

        fetch(`${this.host}${action}`, { 
            method: 'post',
            headers: {
                ...this.headers,
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : null,
        })
        .then(async res => {
            const meta = {
                status: res.status,
                data: await res.json()
            }

            if (!res.ok && (res.status === 401)) {
                this.tokenExpired()
                return
            }

            callback(meta)
        })
        .catch(async error => {
            console.error(`post ${action} errored: ${error}`)
            callback(null)
        })
    }

    /**
     * Add a new resource using the backend API asynchoronously using async/await methodology.
     * @param action The URL invluding any path or query params.
     * @param body The resource meta data to include in the API request content body.
     * @returns A promise to be handled by the callee.
     */
    public postAsync = async (action: string, body: object): Promise<any> => {
        this.withAuth()
        this.withOffset()
        this.withLot()
        
        return await fetch(`${this.host}${action}`, { 
            method: 'post',
            headers: {
                ...this.headers,
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : null
        })
        .then(async res => {
            return {
                status: res.status,
                data: await res.json()
            }
        })
    }

    /**
     * Fully modify an existing resource using the backend API. Content body (params) 
     * contains all fields reuquired to completely modify the resource.
     * @param action The URL invluding any path or query params.
     * @param body The resource meta data to include in the API request content body.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public put = (action: string, body: object, callback): void => {
        this.withAuth()
        this.withOffset()
        this.withLot()

        fetch(`${this.host}${action}`, { 
            method: 'put',
            headers: {
                ...this.headers,
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : null
        })
        .then(async res => {
            const meta = {
                status: res.status,
                data: await res.json()
            }

            if (!res.ok && (res.status === 401)) {
                this.tokenExpired()
                return
            }

            callback(meta)
        })
        .catch(async error => {
            console.error(`put ${action} errored: ${error}`)
            callback(null)
        })
    }

    /**
     * Partially modify an existing resource using the backend API.
     * @param action The URL invluding any path or query params.
     * @param body The resource meta data to include in the API request content body.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public patch = (action: string, body: object, callback): void => {
        this.withAuth()
        this.withOffset()
        this.withLot()

        fetch(`${this.host}${action}`, { 
            method: 'PATCH',
            headers: {
                ...this.headers,
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : null
        })
        .then(async res => {
            const meta = {
                status: res.status,
                data: await res.json()
            }

            if (!res.ok && (res.status === 401)) {
                this.tokenExpired()
                return
            }

            callback(meta)
        })
        .catch(async error => {
            console.error(`patch ${action} errored: ${error}`)
            callback(null)
        })
    }

    /**
     * Read the details an existing resource using the backend API.
     * @param action The URL invluding any path or query params.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public get = (action: string, callback): void => {
        this.withAuth()
        this.withOffset()
        this.withLot()
        
        fetch(`${this.host}${action}`, { 
            method: 'get',
            headers: this.headers
        })
        .then(async res => {
            const meta = {
                status: res.status,
                data: await res.json()
            }

            if (!res.ok) {
                if (res.status === 401) {
                    this.tokenExpired()
                    return
                }

                this.userDeactivated(meta.data)
            }

            callback(meta)
        })
        .catch(async error => {
            console.error(`get ${action} errored: ${error}`);
            callback(null)
        })
    }

    /**
     * Read the details an existing resource using the backend API.
     * @param action The URL invluding any path or query params.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public getAsync = async (action: string): Promise<any> => {
        this.withAuth()
        this.withOffset()
        this.withLot()

        return await fetch(`${this.host}${action}`, { 
            method: 'get',
            headers: this.headers 
        })
        .then(async res => {
            return {
                status: res.status,
                data: await res.json()
            }
        })
    }

    /**
     * Delete an existing resource using the backend API.
     * @param action The URL invluding any path or query params.
     * @param callback An optional listener to be notified when the request completes. Includes the response or null if failure.
     */
    public discard = (action: string, callback): void => {
        this.withAuth()
        this.withOffset()
        this.withLot()

        fetch(`${this.host}${action}`, { 
            method: 'delete',
            headers: this.headers
        })
        .then(async res => {
            const meta = {
                status: res.status,
                data: await res.json()
            }

            if (!res.ok && (res.status === 401)) {
                this.tokenExpired()
                return
            }

            callback(meta)
        })
        .catch(async error => {
            console.error(`delete ${action} errored: ${error}`);
            callback(null)
        })
    }

    private tokenExpired = (): void => {
        localStorage.clear()
        sessionStorage.clear()
        this.token = ''

        window.location.href = '#/login'
        window.location.reload()
    }

    private userDeactivated = (meta): void => {
        if (!meta || !meta.error) {
            return
        }

        const message = meta.error
        if (!message) {
            return
        }

        if ((message.indexOf('The user has been deactivated.') === -1)) {
            return
        }

        this.ms.sendMessage("alert", { type: "danger", text: 'The user has been deactivated.' });
        localStorage.removeItem('token')
        localStorage.removeItem('lot')
        this.token = ''
        window.location.href = "#/login"
        window.location.reload()
    }

    public getDataFromURL = (url: string, payload: Object, callback) => {
        if (!url.startsWith('http')) {
            url = `${this.host}${url}`
            this.withAuth()
            this.withOffset()
            this.withLot()
        }

        fetch(url, { 
            method: 'post',
            headers: this.headers
        })
        .then(async res => {
            if (!res.ok) {
                const error = await res.json()
                callback({ status: false, error: error, data: null })
                return
            }

            const reader = new FileReader()
            reader.onload = function(e) {
                const result = e.target['result']
                callback({ status: true, data: result })
            }

            const meta = await res.blob()
            reader.readAsDataURL(meta)                
        })
        .catch(async (error) => {
            callback({ status: false, error: error.message, data: null })
        })

    }

}
